import React, { useEffect, useRef, useState } from 'react';
import type { DBCharacter } from '../../../services/CharacterService';
import { ApiClient } from '../../../../services/ApiClient';
import { CharacterPortrait } from '../../components/CharacterPortrait';
import { classColor, formatCallingName, ROSTER_THEME_VARS } from '../../theme';
import { CharacterRosterGear } from './characterRoster/CharacterRosterGear';
import { rosterEquipmentVisuals, rosterLocation } from './characterRoster/characterRosterPresentation';
import './CharacterSelectScreen.css';

interface CharacterSelectScreenProps {
  sessionToken: string;
  autoSelect?: boolean;
  onSelectCharacter: (char: DBCharacter) => void;
  /** Open the creator; `rosterEmpty` says whether there are heroes to come back to. */
  onCreateNew: (context: { rosterEmpty: boolean }) => void;
  onSessionInvalid?: () => void;
  onLogout?: () => void;
  error?: string;
}

/** The roster owns presentation; PreGameFlow remains the sole creation/entry owner. */
export const CharacterSelectScreen: React.FC<CharacterSelectScreenProps> = ({
  sessionToken, autoSelect = false, onSelectCharacter, onCreateNew,
  onSessionInvalid, onLogout, error: externalError,
}) => {
  const [characters, setCharacters] = useState<DBCharacter[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rosterToken, setRosterToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const callbacks = useRef({ onCreateNew, onSelectCharacter, onSessionInvalid });
  callbacks.current = { onCreateNew, onSelectCharacter, onSessionInvalid };
  const session = useRef(sessionToken);
  session.current = sessionToken;
  const mounted = useRef(false);
  const currentCharacters = rosterToken === sessionToken ? characters : [];
  const selected = currentCharacters.find(char => char._id === selectedId) ?? null;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    setLoading(true);
    setError('');
    setCharacters([]);
    setDeletingId(null);
    void ApiClient.fetchCharacters(sessionToken).then((data: DBCharacter[]) => {
      if (!active) return;
      if (data.length === 0) { callbacks.current.onCreateNew({ rosterEmpty: true }); return; }
      if (autoSelect) { callbacks.current.onSelectCharacter(data[0]); return; }
      setCharacters(data);
      setSelectedId(previous => data.some(char => char._id === previous) ? previous : data[0]._id);
    }).catch((failure: unknown) => {
      if (!active) return;
      const message = failure instanceof Error ? failure.message : String(failure);
      if (/403|401/.test(message)) {
        setError('Your session has expired. Returning to login…');
        expiryTimer = setTimeout(() => { if (active) callbacks.current.onSessionInvalid?.(); }, 2000);
      } else {
        setError('Your heroes could not be loaded. Please try again.');
      }
    }).finally(() => { if (active) { setRosterToken(sessionToken); setLoading(false); } });
    return () => { active = false; if (expiryTimer) clearTimeout(expiryTimer); };
  }, [sessionToken, autoSelect, refresh]);

  const abandon = async (char: DBCharacter) => {
    if (deletingId) return;
    if (!window.confirm(`Abandon "${char.name}" (Lvl ${char.level || 1} ${formatCallingName(char.class)})? This permanently deletes the character.`)) return;
    const ownerToken = sessionToken;
    setDeletingId(char._id);
    try {
      await ApiClient.deleteCharacter(ownerToken, char._id);
      if (mounted.current && session.current === ownerToken) setRefresh(value => value + 1);
    } catch {
      if (mounted.current && session.current === ownerToken) {
        setError('This hero could not be abandoned. Please try again.');
      }
    } finally {
      if (mounted.current && session.current === ownerToken) setDeletingId(null);
    }
  };

  return (
    <main className="hero-roster" style={ROSTER_THEME_VARS} aria-labelledby="roster-title">
      <header className="hero-roster__heading">
        <div>
          <span className="hero-roster__eyebrow">Helix 2000 · Your journey</span>
          <h1 id="roster-title">Select Character</h1>
          <p>Choose the hero who will write your next chapter.</p>
        </div>
        {!loading && currentCharacters.length > 0 && <span className="hero-roster__count">{currentCharacters.length} {currentCharacters.length === 1 ? 'hero' : 'heroes'}</span>}
      </header>

      {(externalError || error) && <div className="hero-roster__alert" role="alert">
        {externalError || error}
        {error && !/session/i.test(error) && <button type="button" onClick={() => setRefresh(value => value + 1)}>Try again</button>}
      </div>}

      {loading ? <div className="hero-roster__loading" role="status">Gathering your heroes…</div> : (
        <div className="hero-roster__body">
          <nav className="hero-roster__rail" aria-label="Your characters">
            <div className="hero-roster__rail-heading"><span>Your heroes</span><span>Choose a character</span></div>
            <div className="hero-roster__list pregame-char-grid">
              {currentCharacters.map(char => (
                <button type="button" key={char._id}
                  className="hero-roster__hero" aria-label={`Select ${char.name}`}
                  aria-pressed={selectedId === char._id} onClick={() => setSelectedId(char._id)}
                  style={{ '--hero-accent': classColor(char.class) } as React.CSSProperties}>
                  <span className="hero-roster__portrait" aria-hidden="true">
                    <CharacterPortrait appearance={char.appearance} characterClass={char.class} equipmentVisuals={rosterEquipmentVisuals(char)} size={84} />
                  </span>
                  <span className="hero-roster__hero-copy">
                    <strong>{char.name}</strong>
                    <span>Level {char.level || 1} · {formatCallingName(char.class)}</span>
                    <small>{rosterLocation(char)}</small>
                  </span>
                  <span className="hero-roster__selected-mark" aria-hidden="true">{selectedId === char._id ? '✓' : ''}</span>
                </button>
              ))}
            </div>
            <button type="button" className="hero-roster__create" onClick={() => onCreateNew({ rosterEmpty: false })}><span aria-hidden="true">+</span> Create New Hero</button>
          </nav>

          {selected && <section className="hero-roster__detail" aria-label={`${selected.name} character details`}
            style={{ '--hero-accent': classColor(selected.class) } as React.CSSProperties}>
            <div className="hero-roster__showcase">
              <header className="hero-roster__identity">
                <span className="hero-roster__eyebrow">{formatCallingName(selected.class)} · Level {selected.level || 1}</span>
                <h2>{selected.name}</h2>
                <p><span aria-hidden="true">◇</span> {rosterLocation(selected)}</p>
              </header>
              <div className="hero-roster__stage">
                <div className="hero-roster__orbit" aria-hidden="true" />
                <CharacterPortrait appearance={selected.appearance} characterClass={selected.class}
                  equipmentVisuals={rosterEquipmentVisuals(selected)} size={320} label={`${selected.name}, saved appearance and equipped gear`} />
              </div>
              <span className="hero-roster__stage-caption">Your hero’s saved look and gear</span>
            </div>
            <CharacterRosterGear character={selected} />
            <div className="hero-roster__manage">
              <span>Continue where your journey left off.</span>
              <button type="button" disabled={Boolean(deletingId)} onClick={() => void abandon(selected)}>
                {deletingId === selected._id ? 'Abandoning…' : 'Abandon hero'}
              </button>
            </div>
          </section>}
        </div>
      )}

      <footer className="hero-roster__actions">
        <button type="button" className="hero-roster__logout" onClick={onLogout}>Logout / Switch</button>
        <div className="hero-roster__entry-note">{selected ? `Ready for ${rosterLocation(selected)}` : 'Select a hero to begin'}</div>
        <button type="button" className="hero-roster__enter" disabled={!selected || loading || Boolean(deletingId)}
          onClick={() => { if (selected) onSelectCharacter(selected); }}>Enter World <span aria-hidden="true">→</span></button>
      </footer>
    </main>
  );
};
